import { MenuItem, TextField } from "@mui/material";
import type { ReactNode } from "react";
import { memberAvatarColor, memberInitials } from "../lib/avatar";
import { CategoryIcon } from "../lib/format";
import type { Account, Member } from "../lib/types";
import { useFilterDropdown } from "../hooks/useFilterDropdown";

type SelectSize = "small" | "medium";

type FinanceSelectProps = {
  className?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  filterDropdown?: boolean;
  label: ReactNode;
  name?: string;
  required?: boolean;
  size?: SelectSize;
  startAdornment?: ReactNode;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
};

function joinClassNames(...classNames: Array<string | undefined>) {
  return classNames.filter(Boolean).join(" ");
}

export function accountOwnerName(account: Account, members: Member[]) {
  if (!account.owner_user_id) return "";
  return members.find((member) => member.user_id === account.owner_user_id)?.display_name || "";
}

export function AccountSelectOption({ account, members, showIcon = true }: { account: Account; members: Member[]; showIcon?: boolean }) {
  const ownerName = accountOwnerName(account, members);
  return (
    <span className="records-select-option account-select-option">
      {showIcon ? <CategoryIcon icon={account.icon} color={account.color} /> : null}
      <span>{ownerName ? `${account.name} · ${ownerName}` : account.name}</span>
    </span>
  );
}

export function AccountSelect({
  accounts,
  members,
  emptyLabel,
  className,
  disabled,
  error,
  helperText,
  filterDropdown = false,
  label,
  name,
  required,
  size,
  startAdornment,
  renderValueIcon = true,
  value,
  defaultValue,
  onChange,
}: FinanceSelectProps & {
  accounts: Account[];
  members: Member[];
  emptyLabel: string;
  renderValueIcon?: boolean;
}) {
  const dropdown = useFilterDropdown({ enabled: filterDropdown });
  return (
    <TextField
      select
      className={joinClassNames("finance-entity-select", className)}
      size={size}
      label={label}
      name={name}
      required={required}
      value={value}
      defaultValue={defaultValue}
      disabled={disabled}
      error={error}
      helperText={helperText}
      slotProps={{
        input: { startAdornment },
        inputLabel: { shrink: true },
        select: {
          displayEmpty: true,
          ...(filterDropdown ? { open: dropdown.open, onOpen: dropdown.onOpen, onClose: dropdown.onClose, MenuProps: dropdown.MenuProps } : {}),
          renderValue: (selected) => {
            const account = accounts.find((item) => item.id === String(selected || ""));
            return account ? <AccountSelectOption account={account} members={members} showIcon={renderValueIcon} /> : emptyLabel;
          },
        },
      }}
      onChange={(event) => onChange?.(String(event.target.value))}
    >
      <MenuItem value="">{emptyLabel}</MenuItem>
      {accounts.map((account) => (
        <MenuItem key={account.id} value={account.id}>
          <AccountSelectOption account={account} members={members} />
        </MenuItem>
      ))}
    </TextField>
  );
}

export function MemberAvatar({ member }: { member: Member }) {
  return (
    <span
      className={`account-owner-avatar${member.avatar_data_url ? " has-image" : ""}`}
      style={{ backgroundColor: member.avatar_data_url ? undefined : memberAvatarColor(member) }}
    >
      {member.avatar_data_url ? <img src={member.avatar_data_url} alt="" /> : memberInitials(member)}
    </span>
  );
}

export function UserSelectOption({ member }: { member: Member }) {
  return (
    <span className="records-select-option account-owner-option">
      <MemberAvatar member={member} />
      <span>{member.display_name}</span>
    </span>
  );
}

export function UserSelect({
  members,
  emptyLabel,
  className,
  disabled,
  error,
  helperText,
  filterDropdown = false,
  label,
  name,
  required,
  size,
  startAdornment,
  value,
  defaultValue,
  onChange,
}: FinanceSelectProps & {
  members: Member[];
  emptyLabel: string;
}) {
  const dropdown = useFilterDropdown({ enabled: filterDropdown });
  return (
    <TextField
      select
      className={joinClassNames("finance-entity-select", className)}
      size={size}
      label={label}
      name={name}
      required={required}
      value={value}
      defaultValue={defaultValue}
      disabled={disabled}
      error={error}
      helperText={helperText}
      slotProps={{
        input: { startAdornment },
        inputLabel: { shrink: true },
        select: {
          displayEmpty: true,
          ...(filterDropdown ? { open: dropdown.open, onOpen: dropdown.onOpen, onClose: dropdown.onClose, MenuProps: dropdown.MenuProps } : {}),
          renderValue: (selected) => {
            const member = members.find((item) => item.user_id === String(selected || ""));
            return member ? <UserSelectOption member={member} /> : emptyLabel;
          },
        },
      }}
      onChange={(event) => onChange?.(String(event.target.value))}
    >
      <MenuItem value="">{emptyLabel}</MenuItem>
      {members.map((member) => (
        <MenuItem key={member.user_id} value={member.user_id}>
          <UserSelectOption member={member} />
        </MenuItem>
      ))}
    </TextField>
  );
}
